import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const UserCohortAssignment = () => {
  const navigate = useNavigate();
  // Core state management
  const [organizations, setOrganizations] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [usersInCohort, setUsersInCohort] = useState([]);
  
  // Batch import state
  const [batchMode, setBatchMode] = useState(false);
  const [batchEmails, setBatchEmails] = useState('');
  const [parsedEmails, setParsedEmails] = useState([]);
  const [matchedUsers, setMatchedUsers] = useState([]);
  const [unmatchedEmails, setUnmatchedEmails] = useState([]);
  const [processingBatch, setProcessingBatch] = useState(false);

  // Load organizations on component mount
  useEffect(() => {
    const fetchOrganizations = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/organizations', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error fetching organizations:', error.response?.data?.error);
        setMessage('Error fetching organizations');
      }
    };
    fetchOrganizations();
  }, []);

  // Load cohorts when organization is selected
  useEffect(() => {
    if (selectedOrganizationId) {
      const fetchCohorts = async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await axios.get(`https://genz-staging.feeltiptop.com/api/social_glass/cohorts?organization_id=${selectedOrganizationId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setCohorts(response.data);
          setSelectedCohortId(''); 
          setSelectedUsers([]);
          // Reset batch state
          setBatchMode(false);
          setBatchEmails('');
          setParsedEmails([]);
          setMatchedUsers([]);
          setUnmatchedEmails([]);
        } catch (error) {
          console.error('Error fetching cohorts:', error.response?.data?.error);
          setMessage('Error fetching cohorts');
        }
      };
      fetchCohorts();
    } else {
      setCohorts([]);
    }
  }, [selectedOrganizationId]);

  // Load users when cohort is selected
  useEffect(() => {
    if (selectedCohortId) {
      const fetchUsersInCohort = async () => {
        const token = localStorage.getItem('token');
        try {
          const response = await axios.get(`https://genz-staging.feeltiptop.com/api/social_glass/cohort-users?cohort_id=${selectedCohortId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          setUsersInCohort(response.data);
        } catch (error) {
          console.error('Error fetching users in cohort:', error.response?.data?.error);
        }
      };
      fetchUsersInCohort();
      fetchAllUsers();
    } else {
      setUsersInCohort([]);
      setUsers([]);
    }
  }, [selectedCohortId]);

  // Fetch all users
  const fetchAllUsers = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get('https://genz-staging.feeltiptop.com/api/social_glass/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error.response?.data?.error);
      setMessage('Error fetching users');
      setLoading(false);
    }
  };

  // Handle single user selection
  const handleUserSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  // Process and parse emails for batch import
  const handleProcessEmails = () => {
    setProcessingBatch(true);
    
    // Parse emails from textarea
    const emailList = batchEmails
      .split(/[\n,;]/) // Split by newline, comma, or semicolon
      .map(email => email.trim())
      .filter(email => email.includes('@') && email.length > 0); // Basic validation
    
    setParsedEmails(emailList);
    
    // Match emails with users
    const matched = [];
    const unmatched = [];

    emailList.forEach(email => {
      const user = users.find(u => 
        u.Email?.toLowerCase() === email.toLowerCase() && 
        !usersInCohort.some(cohortUser => cohortUser.UserID === u.UserID)
      );
      
      if (user) {
        matched.push(user);
      } else {
        unmatched.push(email);
      }
    });
    
    setMatchedUsers(matched);
    setUnmatchedEmails(unmatched);
    setProcessingBatch(false);
    
    // Auto-select matched users
    const matchedUserIds = matched.map(user => user.UserID);
    setSelectedUsers([...selectedUsers, ...matchedUserIds]);
  };

  // Filter users based on search term and exclude users already in cohort
  const filteredUsers = users.filter(user => {
    const alreadyInCohort = usersInCohort.some(cohortUser => cohortUser.UserID === user.UserID);
    
    const matchesSearch = searchTerm === '' || 
      user.UserName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.Email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    return !alreadyInCohort && matchesSearch;
  });

  // Handle form submission to add users to cohort
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCohortId || selectedUsers.length === 0) {
      setMessage('Please select a cohort and at least one user');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(
        'https://genz-staging.feeltiptop.com/api/social_glass/assign-users-to-cohort',
        {
          cohort_id: selectedCohortId,
          user_ids: selectedUsers
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      setMessage('Users successfully assigned to cohort');
      setSelectedUsers([]);
      setBatchMode(false);
      setBatchEmails('');
      setParsedEmails([]);
      setMatchedUsers([]);
      setUnmatchedEmails([]);
      
      // Refresh users in cohort
      const updatedUsersInCohort = await axios.get(`https://genz-staging.feeltiptop.com/api/social_glass/cohort-users?cohort_id=${selectedCohortId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsersInCohort(updatedUsersInCohort.data);
      
    } catch (error) {
      setMessage(error.response?.data?.error || 'Error assigning users to cohort');
    } finally {
      setLoading(false);
    }
  };

  // Handle removing user from cohort
  const handleRemoveUserFromCohort = async (userId) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`https://genz-staging.feeltiptop.com/api/social_glass/remove-user-from-cohort`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        data: {
          cohort_id: selectedCohortId,
          user_id: userId
        }
      });
      
      setUsersInCohort(usersInCohort.filter(user => user.UserID !== userId));
      setMessage('User removed from cohort successfully');
    } catch (error) {
      console.error('Error removing user from cohort:', error.response?.data?.error);
      setMessage('Error removing user from cohort');
    } finally {
      setLoading(false);
    }
  };

  // Toggle between individual and batch selection modes
  const toggleBatchMode = () => {
    setBatchMode(!batchMode);
    if (!batchMode) {
      // Switching to batch mode, reset batch-related state
      setBatchEmails('');
      setParsedEmails([]);
      setMatchedUsers([]);
      setUnmatchedEmails([]);
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-8 sm:p-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Assign Users to Cohort</h2>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Organization and Cohort Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-2">
                  Organization
                </label>
                <select 
                  id="organization"
                  value={selectedOrganizationId} 
                  onChange={(e) => setSelectedOrganizationId(e.target.value)} 
                  required
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2.5 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Select Organization</option>
                  {organizations.map(org => (
                    <option key={org.OrganizationID} value={org.OrganizationID}>
                      {org.Name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="cohort" className="block text-sm font-medium text-gray-700 mb-2">
                  Cohort
                </label>
                <select 
                  id="cohort"
                  value={selectedCohortId} 
                  onChange={(e) => setSelectedCohortId(e.target.value)} 
                  required
                  disabled={!selectedOrganizationId}
                  className="block w-full rounded-md border border-gray-300 shadow-sm py-2.5 px-4 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">Select Cohort</option>
                  {cohorts.map(cohort => (
                    <option key={cohort.CohortID} value={cohort.CohortID}>
                      {cohort.Name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {selectedCohortId && (
              <>
                {/* Current Users in Cohort section */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Current Users in Cohort</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Users currently assigned to this cohort ({usersInCohort.length})
                    </p>
                  </div>
                  
                  <div className="overflow-x-auto">
                    {usersInCohort.length > 0 ? (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined At</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {usersInCohort.map(user => (
                            <tr key={user.UserID} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{user.UserName}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.Email}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(user.JoinedAt).toLocaleString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveUserFromCohort(user.UserID)}
                                  className="text-red-600 hover:text-red-900 font-medium"
                                  disabled={loading}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-6 text-center">
                        <p className="text-gray-500 italic">No users currently in this cohort</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Add Users to Cohort section */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-medium leading-6 text-gray-900">Add Users to Cohort</h3>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={toggleBatchMode}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                            !batchMode 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Individual Select
                        </button>
                        <button
                          type="button"
                          onClick={toggleBatchMode}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                            batchMode 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          Batch Import
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    {batchMode ? (
                      // Batch Email Import UI
                      <div className="space-y-6">
                        <div>
                          <label htmlFor="batchEmails" className="block text-sm font-medium text-gray-700 mb-2">
                            Paste Email Addresses
                          </label>
                          <p className="text-xs text-gray-500 mb-2">
                            Paste emails separated by commas, semicolons, or new lines (e.g., from Excel)
                          </p>
                          <textarea
                            id="batchEmails"
                            value={batchEmails}
                            onChange={(e) => setBatchEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com, ..."
                            rows={5}
                            className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleProcessEmails}
                            disabled={!batchEmails.trim() || processingBatch}
                            className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
                          >
                            {processingBatch ? 'Processing...' : 'Process Emails'}
                          </button>
                        </div>
                        
                        {/* Results of batch processing */}
                        {(matchedUsers.length > 0 || unmatchedEmails.length > 0) && (
                          <div className="pt-4 border-t border-gray-200">
                            <h4 className="text-base font-medium text-gray-900 mb-3">Processing Results</h4>
                            
                            {/* Matched users */}
                            {matchedUsers.length > 0 && (
                              <div className="mb-4">
                                <h5 className="text-sm font-medium text-green-700 flex items-center mb-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  {matchedUsers.length} Matched Users
                                </h5>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                  {matchedUsers.map(user => (
                                    <div key={user.UserID} className="border border-green-200 bg-green-50 rounded-md p-2 text-sm">
                                      <div className="font-medium">{user.firstName} {user.lastName}</div>
                                      <div className="text-gray-600">{user.Email}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Unmatched emails */}
                            {unmatchedEmails.length > 0 && (
                              <div>
                                <h5 className="text-sm font-medium text-red-700 flex items-center mb-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  {unmatchedEmails.length} Unmatched Emails
                                </h5>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                  {unmatchedEmails.map((email, index) => (
                                    <div key={index} className="border border-red-200 bg-red-50 rounded-md p-2 text-sm">
                                      {email}
                                    </div>
                                  ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                  Unmatched emails were not found in the system or are already in this cohort
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      // Individual User Selection UI
                      <div className="space-y-6">
                        <div>
                          <label htmlFor="searchUsers" className="block text-sm font-medium text-gray-700 mb-2">
                            Search Users
                          </label>
                          <input
                            type="text"
                            id="searchUsers"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name or email"
                            className="block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-md max-h-96 overflow-y-auto">
                          {loading ? (
                            <div className="text-center py-4">
                              <p className="text-gray-500">Loading users...</p>
                            </div>
                          ) : filteredUsers.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {filteredUsers.map(user => (
                                <div 
                                  key={user.UserID}
                                  className={`border rounded-md p-3 cursor-pointer transition duration-150 ${
                                    selectedUsers.includes(user.UserID) 
                                      ? 'border-blue-500 bg-blue-50' 
                                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                  }`}
                                  onClick={() => handleUserSelect(user.UserID)}
                                >
                                  <div className="flex items-start">
                                    <input
                                      type="checkbox"
                                      checked={selectedUsers.includes(user.UserID)}
                                      onChange={() => {}}
                                      className="h-4 w-4 text-blue-600 mt-1 mr-2 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <div>
                                      <p className="font-medium text-sm">
                                        {user.firstName} {user.lastName}
                                      </p>
                                      <p className="text-xs text-gray-500">{user.UserName}</p>
                                      <p className="text-xs text-gray-500">{user.Email}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic text-center py-4">
                              {searchTerm ? "No matching users found" : "No users available to add"}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Submit button */}
                <div className="flex justify-end pt-4">
                  <button 
                    type="submit"
                    disabled={selectedUsers.length === 0 || loading}
                    className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Processing...' : `Assign ${selectedUsers.length} Users to Cohort`}
                  </button>
                </div>
              </>
            )}
          </form>

          <div
            onClick={() => navigate('/submitpost')}
            className="mt-6 text-blue-600 hover:text-blue-800 cursor-pointer text-lg font-semibold underline text-center"
          >
            Go to Post Submission Page
          </div>
          
          {/* Status message */}
          {message && (
            <div 
              className={`mt-6 p-4 rounded-md text-sm ${
                message.includes('Error') 
                  ? 'bg-red-50 text-red-700 border border-red-200' 
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserCohortAssignment;